import { QualityControlOutput, TKV2_ORDER_ATTACHMENT_API } from '@aloft/toolkit-shared';
import { useQueryClient } from '@tanstack/react-query';

import { useEffect, useRef, useState } from 'react';

import {
  fetchPostReportAPI,
  getTkv2OrderApiQuery,
  getTkv2QualityControlApiQuery,
  useGetTkv2QualityControlAPI,
  usePostTkv2QualityControlAPI,
  usePutTkv2QualityControlAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { useAppraisifyReport } from '../appraisalReport/hooks/useAppraisifyReport.tsx';

const isNotFound = (error: unknown) =>
  typeof error === 'object' && error !== null && 'status' in error && error.status === 404;

export const useQualityControlSync = (orderID: string) => {
  const queryClient = useQueryClient();
  const { fetcherOptions } = useToolkitContext();
  const { appraisalData } = useAppraisifyReport();
  const hasPostedForMissingAttachment = useRef(false);
  const [qualityControl, setQualityControl] = useState<QualityControlOutput>();
  const queryKey = getTkv2QualityControlApiQuery({ pathParams: { orderID } }).queryKey;
  const orderQueryKey = getTkv2OrderApiQuery({ pathParams: { orderID } }).queryKey;

  const qualityControlQuery = useGetTkv2QualityControlAPI(
    { pathParams: { orderID } },
    {
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
  const postQualityControl = usePostTkv2QualityControlAPI();
  const putQualityControl = usePutTkv2QualityControlAPI();

  useEffect(() => {
    if (!qualityControlQuery.data) return;

    const parsed = TKV2_ORDER_ATTACHMENT_API.QUALITY_CONTROL.GET.RESPONSE.parse(
      qualityControlQuery.data,
    );
    setQualityControl(parsed.body);
  }, [qualityControlQuery.data]);

  const runQualityControl = async () => {
    await queryClient.cancelQueries({ queryKey });
    await fetchPostReportAPI({
      ...fetcherOptions,
      pathParams: { orderID },
      body: { data: appraisalData },
    });

    const response = await postQualityControl.mutateAsync({ pathParams: { orderID } });
    const parsed = TKV2_ORDER_ATTACHMENT_API.QUALITY_CONTROL.POST.RESPONSE.parse(response);
    setQualityControl(parsed.body);
    queryClient.setQueryData(queryKey, response);
    await queryClient.invalidateQueries({ queryKey: orderQueryKey });
    return parsed.body;
  };

  useEffect(() => {
    if (!isNotFound(qualityControlQuery.error) || hasPostedForMissingAttachment.current) return;

    hasPostedForMissingAttachment.current = true;
    setQualityControl(undefined);
    runQualityControl().catch(() => {
      hasPostedForMissingAttachment.current = false;
    });
  }, [appraisalData, fetcherOptions, orderID, postQualityControl, qualityControlQuery.error]);

  const updateQualityControl = async (body: QualityControlOutput) => {
    const response = await putQualityControl.mutateAsync({
      pathParams: { orderID },
      body,
    });
    const parsed = TKV2_ORDER_ATTACHMENT_API.QUALITY_CONTROL.PUT.RESPONSE.parse(response);
    setQualityControl(parsed.body);
    queryClient.setQueryData(queryKey, response);
    return parsed.body;
  };

  return {
    qualityControl,
    isRunningReview: postQualityControl.isPending,
    runQualityControl,
    updateQualityControl,
  };
};
